import { Linking, Platform } from "react-native";
import { useLinkTo } from "@react-navigation/native";
import { useDispatch } from "react-redux";
import { useTheme } from "styled-components/native";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import {
  setProductType,
  setSelectedProduct,
} from "@redux/slices/appStateSlice";
import {
  clearCollections,
  resetFiltersPayloadByProduct,
  setCollection,
  setCollectionTabs,
  setDisplayTabs,
  setFiltersPayload,
  setFiltersSet,
  setPayloadData,
  setSectionName,
} from "@redux/slices/searchFilters";
import { getProductColor } from "@utils/product/productTheme";
import { replaceDeeplinkScheme } from "@utils/product/productValidators";

export interface CollectionParent {
  productType?: string;
  sectionName?: string;
  productPrefix?: string;
  productColor?: string;
  title?: string;
  icon?: any;
  id?: any;
}

/**
 * Shared click handling for collection options, used both by the product card
 * submenu (displaySubmenu) and by the collections screen so that the same
 * option behaves identically wherever it is rendered.
 */
const useCollectionNavigation = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const linkTo = useLinkTo();

  const isCollectionOptionClickable = (option: any) => {
    if (!option) return false;
    return Boolean(
      (option.is_collection && option.options?.length > 0) ||
        (option?.component === Routes.InternalPagesScreen &&
          option?.iframeUrl) ||
        option?.deeplink ||
        option?.collection ||
        (option?.sectionName && option?.productType)
    );
  };

  const navigateToType = (item: any) => {
    dispatch(setProductType(item?.productType));
    dispatch(setSelectedProduct(getProductColor(item?.productType)));
    if (theme.isLargeScreen) {
      dispatch(setSectionName(item.sectionName));
    }

    if (item?.deeplink) {
      const url = replaceDeeplinkScheme(
        item?.deeplink,
        process.env.EXPO_PUBLIC_APP_SCHEME
      );
      if (Platform.OS === "web") {
        linkTo(url);
      } else {
        Linking.openURL(url);
      }
      return;
    }

    navigate(
      item.skipFilterScreenForWeb && !theme.isMobile
        ? Routes.FilterSplitScreen
        : Routes.FilterScreen,
      {
        productType: item?.productType,
        fromResultScreen: false,
        sectionName: item.sectionName,
        productPrefix: item.productPrefix ?? item?.productType,
      }
    );
  };

  const openCollection = (
    collectionItem: any,
    parent: CollectionParent = {}
  ) => {
    const productType = collectionItem?.productType ?? parent.productType;
    const productPrefix =
      collectionItem?.productPrefix ?? parent.productPrefix ?? productType;
    const collectionSectionName =
      collectionItem?.sectionName ?? parent.sectionName ?? productType;

    if (collectionItem.displayTabs) {
      dispatch(setCollectionTabs(collectionItem.options));
      dispatch(setDisplayTabs(true));

      const firstCollection = collectionItem.options?.[0];
      const collectionToUse =
        collectionItem.collection || firstCollection?.collection;

      if (collectionToUse) {
        dispatch(setCollection(collectionToUse));
      }

      dispatch(setSectionName(collectionSectionName));

      if (collectionSectionName && firstCollection?.predefined_payload) {
        dispatch(
          setFiltersPayload({
            sectionName: collectionSectionName,
            data: firstCollection.predefined_payload,
          })
        );

        dispatch(
          setPayloadData({
            productType: productType,
            data: {
              [collectionSectionName]: firstCollection.predefined_payload,
            },
          })
        );
      }

      navigate(Routes.SearchResultScreen, {
        productType: productType,
        sectionName: collectionSectionName,
        fromResultScreen: true,
        collection: collectionToUse,
        fromCollections: true,
        productPrefix: productPrefix,
        showCollectionTabs: true,
      });
      return;
    }

    navigate(Routes.CollectionsScreen, {
      productType: productType,
      options: collectionItem.options,
      title: collectionItem.title ?? collectionItem.label,
      sectionName: collectionItem.sectionName ?? parent.sectionName,
      productPrefix: productPrefix,
      hasSubOptions: collectionItem?.hasSubOptions ?? false,
    });
  };

  const selectCollectionOption = (
    option: any,
    parent: CollectionParent = {}
  ) => {
    if (!isCollectionOptionClickable(option)) return;

    const productType = option?.productType ?? parent.productType;
    const productPrefix =
      option?.productPrefix ?? parent.productPrefix ?? productType;
    const sectionName = option?.sectionName ?? parent.sectionName;

    if (option.is_collection && option.options?.length > 0) {
      openCollection(option, parent);
      return;
    }

    if (option?.component === Routes.InternalPagesScreen && option?.iframeUrl) {
      navigate(Routes.InternalPagesScreen, {
        iframeUrl: option.iframeUrl,
      });
      return;
    }

    if (sectionName && (option?.predefined_payload || option?.collection)) {
      dispatch(resetFiltersPayloadByProduct(productType));
      dispatch(clearCollections());

      if (option?.collection) {
        dispatch(setCollection(option.collection));
      }

      dispatch(setProductType(productType));
      dispatch(setSectionName(sectionName));
      dispatch(setSelectedProduct(getProductColor(productType)));

      if (option?.predefined_payload) {
        dispatch(
          setFiltersPayload({
            sectionName: sectionName,
            data: option.predefined_payload,
          })
        );
        dispatch(
          setPayloadData({
            productType: productType,
            data: { [sectionName]: option.predefined_payload },
          })
        );
        dispatch(setFiltersSet());
      }

      navigate(Routes.ExploreNavigator, {
        screen: theme.isMobile
          ? Routes.SearchResultScreen
          : Routes.FilterSplitScreen,
        params: {
          productType: productType,
          sectionName: sectionName,
          filterSplitStep: 2,
          fromResultScreen: true,
          productPrefix: productPrefix,
          collection: option?.collection,
          fromCollections: true,
          skipFilterScreenForWeb: true,
        },
      });
      return;
    }

    navigateToType({
      ...option,
      productType: productType,
      sectionName: sectionName,
      productPrefix: productPrefix,
      title: option?.label ?? option?.name ?? parent.title,
      icon: option?.icon?.data ?? parent.icon,
      productColor: option?.icon?.icon_color ?? parent.productColor,
      id: parent.id,
    });
  };

  return {
    isCollectionOptionClickable,
    navigateToType,
    openCollection,
    selectCollectionOption,
  };
};

export default useCollectionNavigation;
